// ImportProfileContent.js
import * as XLSX from "xlsx";

/**
 * Rebuilds the complex JSON from the flat Excel rows.
 */
const transformRowsToJson = (rows) => {
  const newProfileContent = {};
  
  // Temporary storage for tips, grouped by their *language-independent* IDs
  const tipGroupsMap = {}; // Key: "Profile|Report|GroupHeaderID|GroupSubHeaderID"
  const tipMap = {};       // Key: "Profile|Report|GroupHeaderID|GroupSubHeaderID|Icon"

  for (const row of rows) {
    const profileName = row["Profile Name"];
    const reportType = row["Report Type"];
    
    if (!profileName || !reportType) continue;

    // 1. Ensure profile object exists
    if (!newProfileContent[profileName]) {
      newProfileContent[profileName] = {};
    }

    // 2. Handle the 'others' row
    if (reportType === "others") {
      newProfileContent[profileName].others = {
        href: row["Href"] || ""
      };
      continue; // Go to the next row
    }
    
    const lang = row["Language"];
    if (!lang) continue; // Skip if row is not 'others' but has no language

    // 3. Ensure report type object exists (advanced, dynamic, compact)
    if (!newProfileContent[profileName][reportType]) {
      newProfileContent[profileName][reportType] = {
        about: { header: {}, text: {} },
        tips: [],
      };
    }
    const profileReport = newProfileContent[profileName][reportType];

    // 4. Check what kind of row this is
    const isTipRow = row.hasOwnProperty("Tip_Icon") || row.hasOwnProperty("Tip_Text");
    const isAboutRow = !isTipRow && (row.hasOwnProperty("About_Header") || row.hasOwnProperty("About_Text"));

    if (isAboutRow) {
      // This is an 'About' row.
      if (row.hasOwnProperty("About_Header")) {
        profileReport.about.header[lang] = row["About_Header"] || "";
      }
      if (row.hasOwnProperty("About_Text")) {
        profileReport.about.text[lang] = row["About_Text"] || "";
      }
    } 
    else if (isTipRow) {
      // This is a 'Tip' row.
      
      // --- THIS IS THE FIX ---
      // Use the language-independent IDs to build the group key
      const tipGroupHeaderID = row["Tip_Group_Header_ID"] || "";
      const tipGroupSubHeaderID = row["Tip_Group_SubHeader_ID"] || "";
      const groupKey = `${profileName}|${reportType}|${tipGroupHeaderID}|${tipGroupSubHeaderID}`;
      // --- END OF FIX ---

      const tipHeader = row["Tips_Header"] || "";
      const tipSubHeader = row["Tips_Subheader"] || "";
      const tipText = row["Tip_Text"] || "";
      const tipIcon = row["Tip_Icon"] || "";
      
      // 1. Find or create the Tip Group in our map
      if (!tipGroupsMap[groupKey]) {
        tipGroupsMap[groupKey] = {
          header: {},
          subHeader: {},
          tipsArray: []
        };
        // Add this new group to the final JSON (it's an object reference)
        profileReport.tips.push(tipGroupsMap[groupKey]);
      }
      const tipsGroup = tipGroupsMap[groupKey];
      
      // 2. Add the translated header/subheader
      if (row.hasOwnProperty("Tips_Header")) {
        tipsGroup.header[lang] = tipHeader;
      }
      if (row.hasOwnProperty("Tips_Subheader")) {
        tipsGroup.subHeader[lang] = tipSubHeader;
      }

      // 3. Find or create the Tip (using Icon as the unique key)
      const tipKey = `${groupKey}|${tipIcon}`;
      if (!tipMap[tipKey]) {
        tipMap[tipKey] = {
          text: {},
          svg: tipIcon,
        };
        tipsGroup.tipsArray.push(tipMap[tipKey]);
      }
      
      // 4. Add the language text to this tip
      if (row.hasOwnProperty("Tip_Text")) {
        tipMap[tipKey].text[lang] = tipText;
      }
    }
  }

  return newProfileContent;
};


/**
 * Main import function to be called from your component.
 * @param {Event} e - The file input change event.
 * @returns {Promise<object>} A promise that resolves with the rebuilt JSON object.
 */
export const handleImport = (e) => {
  const file = e.target.files[0];
  if (!file) {
    return Promise.reject(new Error("No file selected"));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const rows = XLSX.utils.sheet_to_json(worksheet);

        const jsonData = transformRowsToJson(rows);
        
        resolve(jsonData);
      } catch (error) {
        console.error("Error processing Excel file:", error);
        reject(new Error("Failed to read the Excel file. Format may be incorrect."));
      }
    };
    reader.onerror = (err) => {
        reject(new Error("Failed to read file."));
    };
    reader.readAsArrayBuffer(file);
  });
};